<script setup>
/**
 * @description 净水厂
 */

import { reactive, ref } from "vue";
import {
  queryInternalControlByType,
  adjustInternalControl,
} from "@/api/internal";
import { ElMessage } from "element-plus";

// 进水ref
const cInFormRef = ref(null);

// 出水ref
const cOutFormRef = ref(null);

// 当前选中的标准的type
const currentType = ref(null);

// 遮罩
const loading = ref(false);

// 进水表单
const cInForm = reactive({
  id: undefined,
  coInCod: "90", //进水Cod(%)（排水）
  coInTotalNitrogen: "90", //进水总氮(%)（排水）
  coInTotalPhosphorus: "90", //进水总磷(%)（排水）
  coInAmmoniaNitrogen: "90", //进水氨氮(%)（排水）
  coInPhStart: "6", //进水ph开始值（排水）
  coInPhEnd: "9", //进水ph结束值（排水）
  coInWaterTemperature: "12", //进水水温（排水）
});

// 出水表单
const cOutForm = reactive({
  coOutCod: "90", //出水Cod(%)（排水）
  coOutTotalNitrogen: "90", //出水总氮(%)（排水）
  coOutTotalPhosphorus: "90", //出水总磷(%)（排水）
  coOutAmmoniaNitrogenLow: "90", //出水氨氮(%)（排水）
  coOutAmmoniaNitrogenHigh: "90", //出水氨氮(%)（排水）
  coOutPhStart: "6", //出水ph开始值（排水）
  coOutPhEnd: "9", //出水ph结束值（排水）
});

// 自定义表单校验
const floatReg = /^[0-9]*$/;
const validatePosNumber = (rule, value, callback) => {
  if (!["cInPh", "cOutPh", "cOutAmmoniaNitrogen"].includes(rule.field)) {
    if (!value) {
      callback(new Error("请输入内容"));
    } else if (floatReg.test(value)) {
      callback();
    } else {
      callback(new Error("输入内容必须是正数"));
    }
  }
};
const validateMergeNum = (rule, value, callback) => {
  switch (rule.field) {
    case "cInPh":
      rulePh(cInForm, "coInPhStart", "coInPhEnd", callback);
      break;
    case "cOutPh":
      rulePh(cOutForm, "coOutPhStart", "coOutPhEnd", callback);
      break;
    case "cOutAmmoniaNitrogen":
      rulePh(
        cOutForm,
        "coOutAmmoniaNitrogenLow",
        "coOutAmmoniaNitrogenHigh",
        callback
      );
      break;
    default:
      break;
  }
};

// 表单校验
const cInFormRule = reactive({
  coInCod: [{ validator: validatePosNumber, trigger: "blur" }],
  coInTotalNitrogen: [{ validator: validatePosNumber, trigger: "blur" }],
  coInTotalPhosphorus: [{ validator: validatePosNumber, trigger: "blur" }],
  coInAmmoniaNitrogen: [{ validator: validatePosNumber, trigger: "blur" }],
  cInPh: [{ validator: validateMergeNum, trigger: "blur" }],
  coInWaterTemperature: [{ validator: validatePosNumber, trigger: "blur" }],
});

const cOutFormRule = reactive({
  coOutCod: [{ validator: validatePosNumber, trigger: "blur" }],
  coOutTotalNitrogen: [{ validator: validatePosNumber, trigger: "blur" }],
  coOutTotalPhosphorus: [{ validator: validatePosNumber, trigger: "blur" }],
  cOutAmmoniaNitrogen: [{ validator: validateMergeNum, trigger: "blur" }],
  cOutPh: [{ validator: validateMergeNum, trigger: "blur" }],
});

const rulePh = (form, start, end, callback) => {
  const floatReg = /^[0-9]*$/;
  let num1 = form[start];
  let num2 = form[end];
  if (num1 === "" || num1 === null || num2 === "" || num2 === null) {
    callback("请输入内容");
  } else if (
    Number(num1) <= Number(num2) &&
    start !== "coOutAmmoniaNitrogenLow" &&
    end !== "coOutAmmoniaNitrogenHigh"
  ) {
    callback("上限不能小于等于下限!");
  } else if (floatReg.test(num1) && floatReg.test(num2)) {
    callback();
  } else {
    callback(new Error("输入内容必须是正数"));
  }
};

// 获取净水厂标准
const getStandardInfo = (type) => {
  currentType.value = type;
  loading.value = true;
  queryInternalControlByType(type)
    .then((res) => {
      if (JSON.stringify(res.data) !== "{}") {
        for (const key in cInForm) {
          cInForm[key] = res.data[key];
        }
        for (const key in cOutForm) {
          cOutForm[key] = res.data[key];
        }
      } else {
        cInFormRef.value?.resetFields();
        cOutFormRef.value?.resetFields();
        cInForm.coInPhStart = "6";
        cInForm.coInPhEnd = "9";
        cOutForm.coOutAmmoniaNitrogenLow = "90";
        cOutForm.coOutAmmoniaNitrogenHigh = "90";
        cOutForm.coOutPhStart = "6";
        cOutForm.coOutPhEnd = "9";
      }
    })
    .finally(() => {
      loading.value = false;
    });
};

// 提交
const formSubmit = (type) => {
  currentType.value = type;
  cInFormRef.value?.validate((valid) => {
    if (valid) {
      cOutFormRef.value?.validate((outValid) => {
        if (outValid) {
          adjustInternalControl({
            ...cInForm,
            ...cOutForm,
            type: currentType.value,
          }).then((res) => {
            if (res.code == "2000") {
              ElMessage.success("操作成功");
              // 目的是为了将新增后的id赋值给表单 防止多次点击只触发新增操作
              getStandardInfo(currentType.value);
            }
          });
        }
      });
    }
  });
};

// 函数暴露给父组件调用
defineExpose({
  getStandardInfo,
  formSubmit,
});
</script>

<template>
  <div v-loading="loading">
    <div class="my_line_box">
      <el-divider direction="vertical" class="my_line"></el-divider>
      <span class="my_line_txt">净水厂内控标准</span>
    </div>
    <div class="formTitle">进水</div>
    <el-form
      ref="cInFormRef"
      :model="cInForm"
      :rules="cInFormRule"
      label-position="top"
      label-width="80px"
    >
      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="CODcr" prop="coInCod">
            <el-input placeholder="请输入" v-model="cInForm.coInCod">
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="TN" prop="coInTotalNitrogen">
            <el-input placeholder="请输入" v-model="cInForm.coInTotalNitrogen">
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="TP" prop="coInTotalPhosphorus">
            <el-input
              placeholder="请输入"
              v-model="cInForm.coInTotalPhosphorus"
            >
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="NH3N" prop="coInAmmoniaNitrogen">
            <el-input
              placeholder="请输入"
              v-model="cInForm.coInAmmoniaNitrogen"
            >
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="pH" prop="cInPh">
            <el-row :gutter="10">
              <el-col :span="12">
                <el-input placeholder="请输入" v-model="cInForm.coInPhStart">
                  <template #prepend>上限:</template>
                </el-input>
              </el-col>
              <el-col :span="12">
                <el-input placeholder="请输入" v-model="cInForm.coInPhEnd">
                  <template #prepend>下限:</template>
                </el-input>
              </el-col>
            </el-row>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="温度" prop="coInWaterTemperature">
            <el-input
              placeholder="请输入"
              v-model="cInForm.coInWaterTemperature"
            >
              <template #append>℃</template>
            </el-input>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <div class="formTitle">出水</div>
    <el-form
      ref="cOutFormRef"
      :model="cOutForm"
      :rules="cOutFormRule"
      label-position="top"
      label-width="80px"
    >
      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="CODcr" prop="coOutCod">
            <el-input placeholder="请输入" v-model="cOutForm.coOutCod">
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="TN" prop="coOutTotalNitrogen">
            <el-input
              placeholder="请输入"
              v-model="cOutForm.coOutTotalNitrogen"
            >
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="TP" prop="coOutTotalPhosphorus">
            <el-input
              placeholder="请输入"
              v-model="cOutForm.coOutTotalPhosphorus"
            >
              <template #append>%</template>
            </el-input>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="NH3N" prop="cOutAmmoniaNitrogen">
            <template v-if="[2, 3].includes(currentType)">
              <el-input
                placeholder="请输入"
                v-model="cOutForm.coOutAmmoniaNitrogenLow"
              >
                <template #append>%</template>
              </el-input>
            </template>
            <template v-else>
              <el-row :gutter="10">
                <el-col :span="12">
                  <el-input
                    placeholder="请输入"
                    v-model="cOutForm.coOutAmmoniaNitrogenHigh"
                  >
                    <template #prepend>大于12℃</template>
                  </el-input>
                </el-col>
                <el-col :span="12">
                  <el-input
                    placeholder="请输入"
                    v-model="cOutForm.coOutAmmoniaNitrogenLow"
                  >
                    <template #prepend>小于等于12℃</template>
                  </el-input>
                </el-col>
              </el-row>
            </template>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="pH" prop="cOutPh">
            <el-row :gutter="10">
              <el-col :span="12">
                <el-input placeholder="请输入" v-model="cOutForm.coOutPhStart">
                  <template #prepend>上限:</template>
                </el-input>
              </el-col>
              <el-col :span="12">
                <el-input placeholder="请输入" v-model="cOutForm.coOutPhEnd">
                  <template #prepend>下限:</template>
                </el-input>
              </el-col>
            </el-row>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
  </div>
</template>

<style lang="scss" scoped>
.my_line_box {
  display: flex;
  align-items: center;
  margin-top: 10px;
  .my_line {
    background-color: #3f78f7;
    width: 4px;
    height: 14px;
  }
  .my_line_txt {
    font-family: "PingFang SC";
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    text-align: justify;
    line-height: 14px;
  }
}

.formTitle {
  font-size: 14px;
  text-align: center;
  background: #f2f3f4;
  margin: 20px 5px;
}
</style>
